import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  sharePublicAPI,
  createShareGuestClient,
  getUploadsBaseUrl,
} from '../services/api';
import { Project, Bug, BugStats } from '../types';
import ImageViewerModal from '../components/ImageViewerModal';

interface BugImage {
  id: string;
  name: string;
  url: string;
  file?: File;
}

function resolveImageUrl(path: string): string {
  if (path.startsWith('http')) return path;
  return `${getUploadsBaseUrl()}${path}`;
}

const ShareGuest: React.FC = () => {
  const { token: urlToken } = useParams<{ token: string }>();
  const storageKey = urlToken ? `bugcms_share_${urlToken}` : '';

  const [linkValid, setLinkValid] = useState<boolean | null>(null);
  const [projectTitle, setProjectTitle] = useState('');
  const [pin, setPin] = useState('');
  const [unlockError, setUnlockError] = useState('');
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [project, setProject] = useState<Project | null>(null);
  const [bugs, setBugs] = useState<Bug[]>([]);
  const [stats, setStats] = useState<BugStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [unlocking, setUnlocking] = useState(false);
  const [error, setError] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImages, setSelectedImages] = useState<string[]>([]);
  const [newBug, setNewBug] = useState({
    type: '',
    location: '',
    content: '',
    remark: '',
  });
  const [bugImages, setBugImages] = useState<BugImage[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalBugs, setTotalBugs] = useState(0);
  const pageSize = 15;
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!urlToken) return;
    let cancelled = false;
    (async () => {
      try {
        const info = await sharePublicAPI.getInfo(urlToken);
        if (cancelled) return;
        setLinkValid(info.valid);
        setProjectTitle(info.projectName || '');
        if (!info.valid) {
          setLoading(false);
          return;
        }
        const stored = sessionStorage.getItem(storageKey);
        if (stored) {
          setAccessToken(stored);
        }
      } catch {
        if (!cancelled) {
          setLinkValid(false);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [urlToken, storageKey]);

  const fetchGuestData = useCallback(
    async (page: number, token: string) => {
      const client = createShareGuestClient(token);
      const [projectData, bugsData, statsData] = await Promise.all([
        client.getProject(),
        client.getBugs(page, pageSize),
        client.getStats(),
      ]);
      setProject(projectData);
      setBugs(bugsData.bugs);
      setTotalPages(bugsData.totalPages);
      setTotalBugs(bugsData.total);
      setCurrentPage(bugsData.currentPage);
      setStats(statsData);
    },
    [pageSize]
  );

  useEffect(() => {
    if (!accessToken || !urlToken || linkValid !== true) return;
    let cancelled = false;
    (async () => {
      try {
        await fetchGuestData(1, accessToken);
        sessionStorage.setItem(storageKey, accessToken);
        setError('');
      } catch (err: any) {
        sessionStorage.removeItem(storageKey);
        if (!cancelled) {
          setAccessToken(null);
          setError(err.response?.data?.message || '分享已失效，请重新输入分享码');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [accessToken, urlToken, linkValid, storageKey, fetchGuestData]);

  const handlePageChange = (page: number) => {
    if (!accessToken) return;
    setCurrentPage(page);
    fetchGuestData(page, accessToken).catch((err: any) => {
      setError(err.response?.data?.message || '加载失败');
    });
  };

  const handleUnlock = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!urlToken) return;
    setUnlockError('');
    setUnlocking(true);
    try {
      const { accessToken: at } = await sharePublicAPI.unlock(urlToken, pin.trim());
      setAccessToken(at);
      setPin('');
    } catch (err: any) {
      setUnlockError(err.response?.data?.message || '验证失败');
    } finally {
      setUnlocking(false);
    }
  };

  const handleViewImages = (images: string[]) => {
    setSelectedImages(images.map(resolveImageUrl));
    setShowImageModal(true);
  };

  const handleImageUpload = (file: File) => {
    if (!file.type.startsWith('image/')) {
      setError('请选择图片文件');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError('图片文件大小不能超过5MB');
      return;
    }
    const imageUrl = URL.createObjectURL(file);
    const imageId = Date.now().toString();
    setBugImages((prev) => [...prev, { id: imageId, name: file.name, url: imageUrl, file }]);
  };

  const removeImage = (imageId: string) => {
    setBugImages((prev) => {
      const image = prev.find((img) => img.id === imageId);
      if (image && image.url.startsWith('blob:')) {
        URL.revokeObjectURL(image.url);
      }
      return prev.filter((img) => img.id !== imageId);
    });
  };

  const closeCreateBugModal = () => {
    setBugImages((prev) => {
      prev.forEach((img) => {
        if (img.url.startsWith('blob:')) {
          URL.revokeObjectURL(img.url);
        }
      });
      return [];
    });
    setNewBug({ type: '', location: '', content: '', remark: '' });
    setShowCreateForm(false);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files) {
      Array.from(files).forEach(handleImageUpload);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  useEffect(() => {
    const handlePaste = (e: ClipboardEvent) => {
      if (!showCreateForm) return;
      const items = e.clipboardData?.items;
      if (!items) return;
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.type.indexOf('image') !== -1) {
          const file = item.getAsFile();
          if (file) handleImageUpload(file);
          break;
        }
      }
    };
    document.addEventListener('paste', handlePaste);
    return () => document.removeEventListener('paste', handlePaste);
  }, [showCreateForm]);

  const handleCreateBug = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!accessToken || !project) return;
    setError('');
    try {
      const client = createShareGuestClient(accessToken);
      const formData = new FormData();
      formData.append('type', newBug.type);
      formData.append('location', newBug.location);
      formData.append('content', newBug.content);
      formData.append('remark', newBug.remark);
      bugImages.forEach((image) => {
        if (image.file) {
          formData.append('images', image.file);
        }
      });
      await client.createWithImages(formData);
      closeCreateBugModal();
      await fetchGuestData(1, accessToken);
      setCurrentPage(1);
    } catch (err: any) {
      setError(err.response?.data?.message || '创建BUG失败');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto" />
          <p className="mt-4 text-gray-600">加载中...</p>
        </div>
      </div>
    );
  }

  if (linkValid === false) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">分享链接无效</h2>
          <p className="text-gray-600 mb-6">链接已过期或已被作废，请联系项目负责人获取新链接。</p>
          <Link to="/login" className="text-indigo-600 hover:text-indigo-500">
            前往登录
          </Link>
        </div>
      </div>
    );
  }

  if (!accessToken) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="bg-white shadow rounded-lg p-8 max-w-md w-full">
          <h2 className="text-xl font-semibold text-gray-900 mb-1">查看项目 BUG</h2>
          <p className="text-sm text-gray-600 mb-6">
            {projectTitle ? `项目：${projectTitle}` : '请输入负责人提供的 4 位数字分享码'}
          </p>
          <form onSubmit={handleUnlock} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">4 位数字分享码</label>
              <input
                type="text"
                inputMode="numeric"
                pattern="\d{4}"
                maxLength={4}
                value={pin}
                onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md tracking-widest text-center text-lg"
                placeholder="••••"
                autoComplete="off"
              />
            </div>
            {unlockError && (
              <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded px-3 py-2">{unlockError}</div>
            )}
            <button
              type="submit"
              disabled={pin.length !== 4 || unlocking}
              className="w-full bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-300 text-white py-2 rounded-md font-medium"
            >
              {unlocking ? '验证中…' : '进入'}
            </button>
          </form>
          <p className="mt-6 text-xs text-gray-500 text-center">
            客户视图：可查看与新增 BUG，不可标记完成或删除。
          </p>
          <div className="mt-4 text-center">
            <Link to="/login" className="text-sm text-indigo-600 hover:text-indigo-500">
              内部人员登录
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto" />
          <p className="mt-4 text-gray-600">加载项目…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <nav className="bg-white shadow-sm border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16 items-center">
            <div>
              <span className="text-xs text-amber-700 bg-amber-50 px-2 py-1 rounded mr-2">客户分享视图</span>
              <h1 className="text-xl font-semibold text-gray-900 inline">{project.name}</h1>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setShowCreateForm(true)}
                className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-md text-sm font-medium"
              >
                添加 BUG
              </button>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">{error}</div>
          )}

          <div className="bg-white shadow rounded-lg p-6 min-h-[60vh]">
            <p className="text-gray-600 mb-4">{project.description || '暂无描述'}</p>

            {stats && (
              <div className="grid grid-cols-4 gap-4 mb-6">
                <div className="text-center">
                  <div className="text-2xl font-bold text-gray-900">{stats.total}</div>
                  <div className="text-sm text-gray-500">总计</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-yellow-600">{stats.pending}</div>
                  <div className="text-sm text-gray-500">未修改</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-600">{stats.fixed}</div>
                  <div className="text-sm text-gray-500">已修改</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-gray-600">{stats.shelved}</div>
                  <div className="text-sm text-gray-500">搁置</div>
                </div>
              </div>
            )}

            <table className="min-w-full divide-y divide-gray-200 overflow-visible">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase w-16">序号</th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase w-24">类型</th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase w-24">位置</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">内容</th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase w-20">提交人</th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase w-24">状态</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {bugs.map((bug) => (
                  <tr key={bug.id}>
                    <td className="px-3 py-4 text-sm font-medium text-gray-900">#{bug.serialNumber}</td>
                    <td className="px-3 py-4 text-sm text-gray-900">{bug.type}</td>
                    <td className="px-3 py-4 text-sm text-gray-900">{bug.location}</td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      <div>{bug.content}</div>
                      {bug.remark && <div className="text-gray-500 text-xs mt-1">备注: {bug.remark}</div>}
                      {bug.images && bug.images.length > 0 && (
                        <button
                          type="button"
                          onClick={() => handleViewImages(bug.images!)}
                          className="mt-2 text-indigo-600 text-xs"
                        >
                          查看图片 ({bug.images.length})
                        </button>
                      )}
                    </td>
                    <td className="px-3 py-4 text-sm text-gray-900">{bug.creatorName || '—'}</td>
                    <td className="relative z-0 px-3 py-4 align-middle overflow-visible">
                      <div className="group relative z-10 inline-block max-w-full text-left">
                        <span
                          className={`inline-flex cursor-default px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            bug.status === 'pending'
                              ? 'bg-yellow-100 text-yellow-800'
                              : bug.status === 'fixed'
                                ? 'bg-green-100 text-green-800'
                                : 'bg-gray-100 text-gray-800'
                          }`}
                          title={
                            bug.status === 'fixed'
                              ? `修改人：${bug.fixedByName || '未记录'}`
                              : bug.status === 'shelved'
                                ? `搁置说明：${bug.shelvedReason || '无'}`
                                : '未修改'
                          }
                        >
                          {bug.status === 'pending' ? '未修改' : bug.status === 'fixed' ? '已修改' : '搁置'}
                        </span>
                        {bug.status === 'fixed' && (
                          <div
                            role="tooltip"
                            className="pointer-events-none absolute left-1/2 bottom-full z-[100] mb-1 w-max max-w-xs -translate-x-1/2 rounded-xl border border-gray-200 bg-white px-3 py-2 text-left text-xs text-gray-700 shadow-lg opacity-0 invisible transition-opacity duration-200 group-hover:opacity-100 group-hover:visible whitespace-pre-line"
                          >
                            <span className="font-medium text-gray-500">修改人</span>
                            <br />
                            {bug.fixedByName || '未记录'}
                          </div>
                        )}
                        {bug.status === 'shelved' && (
                          <div
                            role="tooltip"
                            className="pointer-events-none absolute left-1/2 bottom-full z-[100] mb-1 w-max max-w-xs -translate-x-1/2 rounded-xl border border-gray-200 bg-white px-3 py-2 text-left text-xs text-gray-700 shadow-lg opacity-0 invisible transition-opacity duration-200 group-hover:opacity-100 group-hover:visible whitespace-pre-line"
                          >
                            <span className="font-medium text-gray-500">搁置说明</span>
                            <br />
                            {bug.shelvedReason || '无'}
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {bugs.length === 0 && (
              <div className="text-center py-12 text-gray-500">暂无 BUG 记录</div>
            )}

            {totalPages > 1 && (
              <div className="mt-6 flex justify-between items-center text-sm">
                <span className="text-gray-600">
                  第 {((currentPage - 1) * pageSize) + 1}–{Math.min(currentPage * pageSize, totalBugs)} 条，共 {totalBugs} 条
                </span>
                <div className="flex gap-2">
                  <button
                    type="button"
                    disabled={currentPage === 1}
                    onClick={() => handlePageChange(currentPage - 1)}
                    className="px-3 py-1 border rounded disabled:opacity-40"
                  >
                    上一页
                  </button>
                  <button
                    type="button"
                    disabled={currentPage === totalPages}
                    onClick={() => handlePageChange(currentPage + 1)}
                    className="px-3 py-1 border rounded disabled:opacity-40"
                  >
                    下一页
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {showCreateForm && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black bg-opacity-50"
          role="dialog"
          aria-modal="true"
          aria-labelledby="share-create-bug-title"
          onClick={closeCreateBugModal}
        >
          <div
            className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex-shrink-0 flex justify-between items-center px-6 py-4 border-b border-gray-200">
              <h3 id="share-create-bug-title" className="text-lg font-medium text-gray-900">
                添加新 BUG
              </h3>
              <button
                type="button"
                onClick={closeCreateBugModal}
                className="text-gray-400 hover:text-gray-600 text-xl leading-none"
                aria-label="关闭"
              >
                ✕
              </button>
            </div>
            <div className="overflow-y-auto px-6 py-4">
              <form onSubmit={handleCreateBug} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">BUG 类型</label>
                    <select
                      value={newBug.type}
                      onChange={(e) => setNewBug({ ...newBug, type: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      required
                    >
                      <option value="">请选择类型</option>
                      {project.bugTypes.map((type) => (
                        <option key={type} value={type}>
                          {type}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">出现位置</label>
                    <select
                      value={newBug.location}
                      onChange={(e) => setNewBug({ ...newBug, location: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      required
                    >
                      <option value="">请选择位置</option>
                      {project.bugLocations.map((loc) => (
                        <option key={loc} value={loc}>
                          {loc}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">BUG 描述</label>
                  <textarea
                    value={newBug.content}
                    onChange={(e) => setNewBug({ ...newBug, content: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    rows={3}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">备注</label>
                  <textarea
                    value={newBug.remark}
                    onChange={(e) => setNewBug({ ...newBug, remark: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    rows={2}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">相关图片</label>
                  <div
                    className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center cursor-pointer hover:border-indigo-400"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <p className="text-sm text-gray-500">点击选择或 Ctrl+V 粘贴截图（单张 ≤5MB）</p>
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      accept="image/*"
                      onChange={handleFileSelect}
                      className="hidden"
                    />
                  </div>
                </div>
                {bugImages.length > 0 && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {bugImages.map((image) => (
                      <div key={image.id} className="relative group">
                        <img src={image.url} alt="" className="w-full h-24 object-cover rounded border" />
                        <button
                          type="button"
                          onClick={() => removeImage(image.id)}
                          className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 text-xs"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex justify-end gap-2 pt-2">
                  <button
                    type="button"
                    onClick={closeCreateBugModal}
                    className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-md text-sm"
                  >
                    取消
                  </button>
                  <button type="submit" className="px-4 py-2 bg-indigo-600 text-white rounded-md text-sm">
                    提交
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      <ImageViewerModal
        open={showImageModal}
        images={selectedImages}
        onClose={() => setShowImageModal(false)}
      />
    </div>
  );
};

export default ShareGuest;
